/**
 * klubb-picker — modal open/close + client-side filtering.
 * ESM module (loaded with type="module"); auto-inits in the browser and
 * exports pure helpers for unit tests.
 */

export function formatResultNote(count, query) {
  return count + ' treff for «' + query + '»';
}

export function markSelected(scope, slug) {
  const tiles = scope.querySelectorAll('[data-klubb-picker-tile]');
  tiles.forEach((tile) => {
    tile.classList.toggle('is-selected', !!slug && tile.dataset.slug === slug);
  });
}

// D47 — stay on the product page across a club switch when the product is
// eligible for the newly selected club (same matcher + semantics as cart
// reconciliation: blank token list = general product, eligible for all).
// Missing matcher/club/template → false, so the caller falls back to the
// existing catalog navigation and the member is never stranded.
export function stayOnPdp(doc, club, matcher) {
  if (typeof matcher !== 'function' || !club) return false;
  const main = doc.querySelector('[data-template]');
  const template = main ? main.getAttribute('data-template') || '' : '';
  if (template !== 'product' && template.indexOf('product.') !== 0) return false;
  const input = main.querySelector('[data-klubb-eligible-clubs]');
  return matcher(input ? input.value : '', club);
}

/**
 * Fills the «Din klubb» hub card on the picker page from the active club.
 * Server-rendered hidden (Liquid can't read the cookie); reveals only when a
 * club is active. Returns true when the card was shown.
 */
export function hydrateMine(doc, club) {
  const card = doc.querySelector('[data-klubb-picker-mine]');
  if (!card) return false;
  if (!club) { card.hidden = true; return false; }

  const name = card.querySelector('[data-klubb-picker-mine-name]');
  if (name) name.textContent = club.name || club.short_name || '';

  const cont = card.querySelector('[data-klubb-picker-mine-continue]');
  if (cont) {
    cont.href = club.catalog_collection_handle
      ? '/collections/' + club.catalog_collection_handle
      : '/';
  }

  const crest = card.querySelector('[data-klubb-picker-mine-crest]');
  if (crest) {
    crest.textContent = '';
    const el = doc.createElement('div');
    el.className = 'klubb-crest klubb-crest--shield';
    // Size comes from CSS (.klubb-picker-page__mine-crest) so mobile can shrink it.
    el.style.cssText = 'background:' + (club.primary_color || '#0a0a0a') +
      ';color:' + (club.secondary_color || '#ffffff');
    if (club.crest_image_url) {
      const img = doc.createElement('img');
      const ratio = Number(club.crest_image_ratio) || 0;
      img.className = ratio >= 0.75 && ratio <= 1.35 ? 'klubb-crest__img klubb-crest__img--cover' : 'klubb-crest__img';
      img.src = club.crest_image_url;
      img.alt = '';
      el.appendChild(img);
    } else {
      const span = doc.createElement('span');
      span.className = 'klubb-crest__initials';
      span.textContent = (club.crest_initials || club.short_name || '').slice(0, 2).toUpperCase();
      el.appendChild(span);
    }
    crest.appendChild(el);
  }

  card.hidden = false;
  return true;
}

(function () {
  'use strict';

  let initialized = false;

  function init() {
    if (initialized) return;
    initialized = true;

    const modals = document.querySelectorAll('[data-klubb-picker-modal]');

    document.addEventListener('klubb:picker:open', () => {
      modals.forEach((m) => {
        m.hidden = false;
        document.documentElement.style.overflow = 'hidden';
        const search = m.querySelector('[data-klubb-picker-search]');
        if (search) setTimeout(() => search.focus(), 50);
      });
    });

    document.addEventListener('klubb:picker:close', () => {
      modals.forEach((m) => { m.hidden = true; });
      document.documentElement.style.overflow = '';
    });

    // Wire close buttons + overlay clicks
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-klubb-picker-close]')) {
        window.klubb && window.klubb.closePicker();
        return;
      }
      const overlay = e.target.closest('[data-klubb-picker-modal]');
      const inner = e.target.closest('.klubb-picker');
      if (overlay && !inner) {
        window.klubb && window.klubb.closePicker();
      }
    });

    // Wire ESC to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') window.klubb && window.klubb.closePicker();
    });

    // Wire all picker scopes (modal + page)
    document.querySelectorAll('[data-klubb-picker-search]').forEach((input) => {
      const scope = input.closest('.klubb-picker');
      if (!scope) return;
      wireScope(scope);
    });
  }

  function wireScope(scope) {
    const search = scope.querySelector('[data-klubb-picker-search]');
    const clear = scope.querySelector('[data-klubb-picker-clear]');
    const filters = scope.querySelector('[data-klubb-picker-filters]');
    const tiles = Array.from(scope.querySelectorAll('[data-klubb-picker-tile]'));
    const empty = scope.querySelector('[data-klubb-picker-empty]');
    const visibleCount = scope.querySelector('[data-klubb-picker-visible-count]');
    const resultNote = scope.querySelector('[data-klubb-picker-result-note]');

    let activeRegion = 'Alle';
    let query = '';

    function applyFilters() {
      const q = query.toLowerCase();
      let visible = 0;
      tiles.forEach((tile) => {
        const matchesRegion = activeRegion === 'Alle' || tile.dataset.region === activeRegion;
        const matchesQuery = !q ||
          (tile.dataset.name && tile.dataset.name.indexOf(q) !== -1) ||
          (tile.dataset.short && tile.dataset.short.indexOf(q) !== -1);
        const show = matchesRegion && matchesQuery;
        tile.hidden = !show;
        if (show) visible++;
      });
      if (visibleCount) visibleCount.textContent = visible;
      if (empty) empty.hidden = visible !== 0;
      if (clear) clear.hidden = query === '';

      // While searching, show the "N treff for «query»" note.
      const searching = query !== '';
      if (resultNote) {
        resultNote.hidden = !searching;
        if (searching) resultNote.textContent = formatResultNote(visible, query);
      }
    }

    if (search) {
      search.addEventListener('input', (e) => {
        query = (e.target.value || '').trim();
        applyFilters();
      });
    }
    if (clear) {
      clear.addEventListener('click', () => {
        if (search) search.value = '';
        query = '';
        applyFilters();
        if (search) search.focus();
      });
    }
    if (filters) {
      filters.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-region]');
        if (!chip) return;
        filters.querySelectorAll('[data-region]').forEach((c) => c.classList.remove('is-active'));
        chip.classList.add('is-active');
        activeRegion = chip.dataset.region;
        applyFilters();
      });
    }

    // Commit a club switch: write the cookie, close the picker, and navigate to
    // the club's catalog (or reload to re-render with the new club context).
    function doSwitch(slug) {
      window.klubb.setActiveClub(slug);
      window.klubb.closePicker();
      const c = window.klubb.activeClub;
      // D47: on a PDP whose product is eligible for the new club, reload in
      // place instead of navigating away — the re-render picks up the new
      // club context. Ineligible or non-PDP → catalog as before.
      const matcher = window.klubbCartReconcile && window.klubbCartReconcile.lineEligibleForClub;
      if (c && stayOnPdp(document, c, matcher)) {
        window.location.reload();
        return;
      }
      if (c && c.catalog_collection_handle) {
        window.location.href = '/collections/' + c.catalog_collection_handle;
      } else {
        window.location.reload();
      }
    }

    // Wire tile selection
    tiles.forEach((tile) => {
      tile.addEventListener('click', () => {
        const slug = tile.dataset.slug;
        if (!slug || !window.klubb) return;
        const current = window.klubb.activeClub;
        // D45: switching away from an existing, different club reconciles the
        // cart first — an itemized confirm dialog, then remove ineligible lines
        // and re-stamp the rest — BEFORE the switch commits. A cancel keeps the
        // member on their current club. Falls back to a plain switch when the
        // reconcile module isn't present or errors.
        if (current && current.slug !== slug && window.klubbCartReconcile && window.klubbCartReconcile.run) {
          window.klubbCartReconcile.run(slug).then((res) => {
            if (res && res.switched === false) return;
            doSwitch(slug);
          }).catch(() => doSwitch(slug));
          return;
        }
        doSwitch(slug);
      });
    });

    // Wire deselect — only visible when a club is currently active. Clears
    // the cookie + window.klubb state and reloads back to the no-club home.
    const deselect = scope.querySelector('[data-klubb-picker-deselect]');
    if (deselect) {
      const hasActive = !!(window.klubb && window.klubb.activeClub);
      deselect.hidden = !hasActive;
      deselect.addEventListener('click', () => {
        if (!window.klubb) return;
        window.klubb.clearActiveClub();
        window.klubb.closePicker();
        window.location.href = '/';
      });
    }

    applyFilters();

    // Mark the currently-active club's tile as selected (server can't read
    // the cookie; window.klubb hydrates it from klubb_active).
    if (window.klubb && window.klubb.activeClub) {
      markSelected(scope, window.klubb.activeClub.slug);
    }

    // «Din klubb» hub card on the picker page (breadcrumb target «Klubber»).
    if (hydrateMine(document, window.klubb ? window.klubb.activeClub : null)) {
      const change = document.querySelector('[data-klubb-picker-mine-change]');
      const search = document.querySelector('.klubb-picker-page [data-klubb-picker-search]');
      if (change) {
        change.addEventListener('click', () => {
          const picker = document.querySelector('.klubb-picker-page .klubb-picker');
          if (picker && picker.scrollIntoView) picker.scrollIntoView({ behavior: 'smooth', block: 'start' });
          if (search) setTimeout(() => search.focus({ preventScroll: true }), 300);
        });
      }
    }
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }
})();
